import React from 'react';
import { Accordion, Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import FilterIcon from '../../Icon/FilterIcon';
import SearchIcon from '../../Icon/SearchIcon';
import './FilterAccordion.css';

/**
 * Componente reutilizável de filtro com Accordion
 * 
 * @param {object} props
 * @param {React.ReactNode} props.children - Conteúdo dos filtros (inputs, selects, etc)
 * @param {function} props.onSearch - Callback executado ao clicar em Buscar
 * @param {function} props.onClear - Callback executado ao clicar em Limpar
 * @param {string} props.title - Título do accordion (padrão: 'filters')
 * @param {string} props.defaultActiveKey - Key ativa por padrão (padrão: "0" - aberto)
 * @param {boolean} props.showQuickDates - Mostrar botões de atalho de data (padrão: false)
 * @param {function} props.onQuickDateSelect - Callback (startDate, endDate) para atalhos de data
 * @param {boolean} props.centerButtons - Centralizar botões (padrão: false)
 */
const FilterAccordion = ({
    children,
    onSearch,
    onClear,
    title = 'filters',
    defaultActiveKey = "0",
    showQuickDates = false,
    onQuickDateSelect,
    centerButtons = false,
    className = ''
}) => {
    const { t } = useTranslation();

    const handleSubmit = (e) => {
        e.preventDefault();
        if (onSearch) {
            onSearch();
        }
    };

    const handleClear = () => {
        if (onClear) {
            onClear();
        }
    };

    // Funções de atalho de data
    const setToday = () => {
        const today = new Date();
        if (onQuickDateSelect) {
            onQuickDateSelect(today, today);
        }
    };

    const setThisWeek = () => {
        const today = new Date();
        const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
        const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));
        if (onQuickDateSelect) {
            onQuickDateSelect(firstDay, lastDay);
        }
    };

    const setThisMonth = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        if (onQuickDateSelect) {
            onQuickDateSelect(firstDay, lastDay);
        }
    };

    const setLastMonth = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
        if (onQuickDateSelect) {
            onQuickDateSelect(firstDay, lastDay);
        }
    };

    return (
        <Accordion className={`accordion accordion-primary-solid filter-accordion ${className}`.trim()} defaultActiveKey={defaultActiveKey}>
            <Accordion.Item className="accordion-item filter-accordion__item">
                <Accordion.Header className="accordion-header rounded-lg filter-accordion__header">
                    <span className="filter-accordion__header-content">
                        <span className="filter-accordion__icon">
                            <FilterIcon />
                        </span>
                        <strong className="filter-accordion__title">{t(title)}</strong>
                    </span>
                </Accordion.Header>
                <Accordion.Collapse className="accordion__body filter-accordion__collapse">
                    <div className="accordion-body filter-accordion__body">
                        <form onSubmit={handleSubmit}>
                            {/* Atalhos de data */}
                            {showQuickDates && onQuickDateSelect && (
                                <Row className="mb-3">
                                    <Col md={12}>
                                        <div className="d-flex gap-2 flex-wrap filter-accordion__quick-actions">
                                            <small className="text-muted me-2 align-self-center filter-accordion__quick-label">
                                                {t('quick_filters')}:
                                            </small>
                                            <Button 
                                                type="button" 
                                                variant="outline-primary" 
                                                size="sm"
                                                className="filter-accordion__quick-button"
                                                onClick={setToday}
                                            >
                                                {t('today')}
                                            </Button>
                                            <Button 
                                                type="button" 
                                                variant="outline-primary" 
                                                size="sm"
                                                className="filter-accordion__quick-button"
                                                onClick={setThisWeek}
                                            >
                                                {t('this_week')}
                                            </Button>
                                            <Button 
                                                type="button" 
                                                variant="outline-primary" 
                                                size="sm"
                                                className="filter-accordion__quick-button"
                                                onClick={setThisMonth}
                                            >
                                                {t('this_month')}
                                            </Button>
                                            <Button 
                                                type="button" 
                                                variant="outline-primary" 
                                                size="sm"
                                                className="filter-accordion__quick-button"
                                                onClick={setLastMonth}
                                            >
                                                {t('last_month')}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* Conteúdo dos filtros (children) */}
                            {children}

                            {/* Botões de ação */}
                            <Row className="mt-4">
                                <Col
                                    md={12}
                                    className={`filter-accordion__actions ${centerButtons ? 'filter-accordion__actions--center' : ''}`}
                                >
                                    <Button type="submit" variant="primary" className="filter-accordion__submit">
                                        <SearchIcon /> {t('search')}
                                    </Button>
                                    {onClear && (
                                        <Button 
                                            type="button" 
                                            variant="outline-secondary"
                                            className="filter-accordion__clear"
                                            onClick={handleClear}
                                        >
                                            <i className="fa-solid fa-rotate-left"></i> {t('clear')}
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        </form>
                    </div>
                </Accordion.Collapse>
            </Accordion.Item>
        </Accordion>
    );
};

export default FilterAccordion;
